import { Module } from 'vuex';
import { ShiftCMSState } from '@/store';
import co2QuizService from '@/services/co2-quiz.service';
import {
  Co2CountryAverage,
  Co2BasketValue,
  Co2QuizQuestion,
  Co2QuizResults,
} from '@/models/co2-quiz.model';
import { SimpleUserCo2QuizAnswer } from '@/models/user.model';

export interface Co2QuizStoreState {
  questions: Co2QuizQuestion[];
  answers: SimpleUserCo2QuizAnswer[];
  values: Co2BasketValue[];
  averages: Co2CountryAverage[];
  results: Co2QuizResults[];
}

export enum Co2QuizStoreMutations {
  SetQuestions = 'co2/SetQuestions',
  SetValues = 'co2/SetValues',
  SetAverages = 'co2/SetAverages',
  UpdateAnswer = 'co2/UpdateAnswer',
  ResetAnswers = 'co2/ResetAnswers',
  SetResults = 'co2/SetResults',
}

export enum Co2QuizStoreActions {
  GetCo2Questions = 'co2/GetCo2Questions',
  GetCo2Question = 'co2/GetCo2Question',
  AddCo2QuizQuestion = 'co2/AddCo2QuizQuestion',
  UpdateCo2QuizQuestion = 'co2/UpdateCo2QuizQuestion',
  GetCo2Values = 'co2/GetCo2Values',
  GetCo2Value = 'co2/GetCo2Value',
  AddCo2BasketValue = 'co2/AddCo2BasketValue',
  UpdateCo2BasketValue = 'co2/UpdateCo2BasketValue',
  GetCo2Averages = 'co2/GetCo2Averages',
  GetCo2Average = 'co2/GetCo2Average',
  AddCo2Average = 'co2/AddCo2Average',
  UpdateCo2Average = 'co2/UpdateCo2Average',
  UpdateQuestionOrder = 'co2/UpdateQuestionOrder',
  UpdateResults = 'co2/UpdateResults',
  DeleteQuestion = 'co2/DeleteQuestion',
}

const sn = (str: Co2QuizStoreActions | Co2QuizStoreMutations): string =>
  str.replace(/co2\//g, '');

export const co2QuizStore: Module<Co2QuizStoreState, ShiftCMSState> = {
  namespaced: true,
  state: {
    questions: [],
    answers: [],
    values: [],
    averages: [],
    results: [],
  },
  mutations: {
    [sn(Co2QuizStoreMutations.SetQuestions)](
      state,
      questions: Co2QuizQuestion[],
    ) {
      state.questions = questions;
    },
    [sn(Co2QuizStoreMutations.SetValues)](state, values: Co2BasketValue[]) {
      state.values = values;
    },
    [sn(Co2QuizStoreMutations.SetAverages)](
      state,
      averages: Co2CountryAverage[],
    ) {
      state.averages = averages;
    },
    [sn(Co2QuizStoreMutations.UpdateAnswer)](
      state,
      answer: SimpleUserCo2QuizAnswer,
    ) {
      state.answers = [
        ...state.answers.filter((a) => a.question.id !== answer.question.id),
        {
          question: { id: answer.question.id },
          answer: answer.answer?.id ? { id: answer.answer.id } : null,
          inputValue: answer.inputValue,
        },
      ];
    },
    [sn(Co2QuizStoreMutations.ResetAnswers)](state) {
      state.answers = [];
      state.results = [];
    },
    [sn(Co2QuizStoreMutations.SetResults)](state, results: Co2QuizResults[]) {
      state.results = results;
    },
  },
  actions: {
    async [sn(Co2QuizStoreActions.GetCo2Questions)]({ commit }) {
      const res = await co2QuizService.getList();
      if (res.status) {
        commit(sn(Co2QuizStoreMutations.SetQuestions), res.data);
      }
    },
    async [sn(Co2QuizStoreActions.GetCo2Question)](_, id: number) {
      const res = await co2QuizService.get(id);
      if (res.status) {
        return res.data;
      }
    },
    async [sn(Co2QuizStoreActions.AddCo2QuizQuestion)](
      _,
      story: Co2QuizQuestion,
    ) {
      const res = await co2QuizService.create({
        ...story,
        id: undefined,
      });

      return res.status;
    },
    async [sn(Co2QuizStoreActions.UpdateCo2QuizQuestion)](
      _,
      story: Co2QuizQuestion,
    ) {
      const res = await co2QuizService.edit(story);

      return res.status;
    },
    async [sn(Co2QuizStoreActions.GetCo2Values)]({ commit }) {
      const res = await co2QuizService.getValues();
      if (res.status) {
        commit(sn(Co2QuizStoreMutations.SetValues), res.data);
      }
    },
    async [sn(Co2QuizStoreActions.GetCo2Value)](_, id: number) {
      const res = await co2QuizService.getValue(id);
      if (res.status) {
        return res.data;
      }
    },
    async [sn(Co2QuizStoreActions.AddCo2BasketValue)](
      _,
      value: Co2BasketValue,
    ) {
      const res = await co2QuizService.createValue({
        ...value,
        id: undefined,
      });

      return res.status;
    },
    async [sn(Co2QuizStoreActions.UpdateCo2BasketValue)](
      _,
      value: Co2BasketValue,
    ) {
      const res = await co2QuizService.editValue(value);

      return res.status;
    },
    async [sn(Co2QuizStoreActions.GetCo2Averages)]({ commit }) {
      const res = await co2QuizService.getAverages();
      if (res.status) {
        commit(sn(Co2QuizStoreMutations.SetAverages), res.data);
      }
    },
    async [sn(Co2QuizStoreActions.GetCo2Average)](_, id: number) {
      const res = await co2QuizService.getAverage(id);
      if (res.status) {
        return res.data;
      }
    },
    async [sn(Co2QuizStoreActions.AddCo2Average)](_, value: Co2CountryAverage) {
      const res = await co2QuizService.createAverage({
        ...value,
        id: undefined,
      });

      return res.status;
    },
    async [sn(Co2QuizStoreActions.UpdateCo2Average)](
      _,
      value: Co2CountryAverage,
    ) {
      const res = await co2QuizService.editAverage(value);

      return res.status;
    },
    async [sn(Co2QuizStoreActions.UpdateQuestionOrder)](
      { commit },
      order: number[],
    ) {
      const res = await co2QuizService.updateOrder(order);

      if (res.status) {
        commit(sn(Co2QuizStoreMutations.SetQuestions), res.data);
      }
    },
    async [sn(Co2QuizStoreActions.UpdateResults)](
      { commit },
      answers: SimpleUserCo2QuizAnswer[],
    ) {
      const res = await co2QuizService.getResults(answers);

      if (res.status) {
        commit(sn(Co2QuizStoreMutations.SetResults), res.data);
      }
    },
    async [sn(Co2QuizStoreActions.DeleteQuestion)]({ dispatch }, id: number) {
      const res = await co2QuizService.delete(id);

      dispatch(sn(Co2QuizStoreActions.GetCo2Questions));

      return res.status;
    },
  },
};
